import React, { useEffect, useMemo, useState } from "react";
import { gql } from "apollo-boost";
import { useQuery } from "@apollo/react-hooks";
import { compose } from "redux";
import {
  withStyles,
  withOverlay,
  withModal,
  withBreakpoint,
  withMobile,
} from "components/common";
import Table from "components/common/Table";

const moment = require("moment");

export const SHOW_DISPUTED_TRANSACTIONS = gql`
  query showDisputedTransactions($user_id: ID!) {
    showDisputedTransactions(user_id: $user_id) {
      status {
        status
        code
        message
      }
      dispute_transactions {
        id
        amount
        description
        status
      }
      user_disputes {
        id
        disputed_amount
        granted_amount
        outcome
        status
      }
    }
  }
`;

const computeColumns = (belowMd) =>
  (belowMd
    ? [
      {
        Header: "Last Activity",
        accessor: (data) =>
          (data.created_at
            ? `${moment.unix(data.created_at / 1000).format("M/D/YYYY LT")}`
            : "unknown"),
      },
      {
        Header: "Description",
        accessor: (data) => (
          <div>
            {data.description}
            {data.disputed ? (
              <i className="fas fa-exclamation-triangle text-red-500 ml-2 text-sm" />
            ) : (
              <></>
            )}
          </div>
        ),
      },
      {
        Header: "Amount",
        accessor: "amount",
      },
    ]
    : [
      {
        Header: "Trans ID",
        accessor: "id",
      },
      {
        Header: "Authorized at",
        accessor: (data) =>
          (data.created_at
            ? `${moment.unix(data.created_at / 1000).format("M/D/YY LT")}`
            : "unknown"),
      },
      {
        Header: "Settled at",
        accessor: (data) =>
          (data.settled_at
            ? `${moment.unix(data.settled_at / 1000).format("M/D/YY LT")}`
            : ""),
      },
      {
        Header: "Amount",
        accessor: "amount",
      },
      {
        Header: "Status",
        accessor: (data) => data.status || "pending",
      },
      {
        Header: "Description",
        accessor: (data) => (
          <div>
            {data.description}
            {data.disputed ? (
              <i className="fas fa-exclamation-triangle text-red-500 ml-2 text-sm" />
            ) : (
              <></>
            )}
          </div>
        ),
      },
      {
        Header: "Tags",
        accessor: (data) => {
          return data.tags?.length ? `${` ${  data.tags.sort().join(", ")  } `}` : "";
        },
      },
    ]);

const UserTransactionsTable = ({
  transactions = [],
  loading,
  breakpointContext: { isBelow },
  mobileContext: { isMobile },
  overlayContext: { openOverlay },
  modalContext: { openModal },
  user,
  submitDispute,
  addTransactionTags,
  removeTransactionTag,
}) => {
  const columns = useMemo(() => computeColumns(isBelow("md")), [isBelow("md")]);
  const { data, refetch: refetchDisputes } = useQuery(SHOW_DISPUTED_TRANSACTIONS, {
    variables: { user_id: user.id },
  });
  const [filteredTransactions, setFilteredTransactions] = useState(
    transactions,
  );

  useEffect(() => {
    setFilteredTransactions(transactions);
    filteredTransactions.sort(
      (a, b) => parseInt(b.created_at) - parseInt(a.created_at),
    );
  }, [transactions]);
  const colorMap = {
    PENDING: "#FFE9AB",
    SETTLED: "#F6FFE5",
    CANCELED: "#DDDDDD",
  };

  const getRowStyle = (row) => {
    return {
      backgroundColor: colorMap[row.original.status],
    };
  };

  return (
    <div className="mt-5">
      <div className="flex">
        <h1 className="text-2xl font-title mb-2 font-light">Transactions</h1>
        <button
          className="btn-green w-40 text-white font-bold rounded-lg outline-0 mx-2 h-10"
          onClick={(e) => {
            openModal(e, "HonorDisputesModal", {
              refetchDisputes,
              data,
            });
          }}
        >
            Honor a Dispute
        </button>
      </div>
      <div className="my-6">
        {loading ? (
          <div>Loading...</div>
        ) : filteredTransactions.length ? (
          <Table
            id="transactions"
            data={filteredTransactions}
            columns={columns}
            getRowStyle={getRowStyle}
            onRowClick={(e, row) => {
              const {
                original: { type, created_at, settled_at, tags } = {},
              } = row;
              row.original.created_at = Number(created_at);
              row.original.settled_at = Number(settled_at);
              if (type !== "payment") {
                if (isMobile) {
                  openOverlay(e, "DisputeOverlay", {
                    row,
                    submitDispute,
                    user,
                    internal: true,
                    addTransactionTags,
                    removeTransactionTag,
                  });
                } else {
                  openModal(
                    e,
                    "DisputeModal",
                    {
                      row,
                      user,
                      submitDispute,
                      internal: true,
                      addTransactionTags,
                      removeTransactionTag,
                    },
                    { disableBackdropClick: true, showCloseButton: false },
                  );
                }
              }
            }}
          />
        ) : (
          <div>Currently there are no transactions.</div>
        )}
      </div>
    </div>
  );
};

export default compose(
  withMobile,
  withStyles,
  withOverlay,
  withModal,
  withBreakpoint,
)(UserTransactionsTable);
